import { Button, Chip, Select } from '@sjuni/ui';
import { FileText, RotateCcw, Trash2, UploadCloud } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import { DocumentDeleteDialog } from './DocumentDeleteDialog';
import { DocumentEditor } from './DocumentEditor';
import { DocumentUploadDialog } from './DocumentUploadDialog';
import { type DocumentCategoryRow, type DocumentRow, formatFileSize } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 and §5.4 — `/documents`.
 *
 * docs/03-DATA-MODEL.md §6 calls documents "the single most-linked resource on
 * the current site", and the migration will land several hundred of them here
 * at once. So the list leads with what an editor uses to find one among that
 * many: the file itself, its category, and — §5.4's "sortable most downloaded
 * view" — how often anybody actually fetches it.
 */
export function DocumentsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <DocumentList /> : <DocumentEditor recordId={rest} />;
}

function DocumentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const list = useListParams();

  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<DocumentRow | null>(null);

  const { data, isPending, isError } = useResourceList<DocumentRow>('documents', list.params);

  const categories = useResourceList<DocumentCategoryRow>('document-categories', {
    per_page: 100,
    sort: 'sort_order',
  });

  const act = useResourceAction<DocumentRow>('documents');

  const mayDelete = can('documents.delete');

  const columns: Column<DocumentRow>[] = [
    {
      key: 'file',
      label: t('documents.columns.file'),
      render: (row) => (
        <span className="inline-flex items-center gap-2">
          {/* The extension rather than a per-format icon: an editor scanning
              for "the PDF" reads three letters faster than a glyph they have
              to learn, and the set of formats is open-ended. */}
          <FileText
            size={16}
            strokeWidth={1.5}
            aria-hidden="true"
            className="shrink-0 text-muted"
          />
          <span className="text-caption font-extrabold uppercase text-muted">{row.file_type}</span>
        </span>
      ),
    },
    {
      key: 'title',
      label: t('documents.columns.title'),
      render: (row) => (
        <div className="min-w-48">
          <span className="font-semibold text-primary">{valueFor(row.title, locale)}</span>
          <span className="mt-0.5 block truncate text-caption text-muted">{row.file_name}</span>

          {row.pending_review ? (
            <Chip tone="gold" className="mt-1">
              {t('editor.publishing.awaitingReview')}
            </Chip>
          ) : null}
        </div>
      ),
    },
    {
      key: 'category',
      label: t('documents.columns.category'),
      render: (row) => (
        <span className="text-secondary">
          {row.category ? valueFor(row.category.name, locale) : '—'}
        </span>
      ),
    },
    {
      key: 'scope',
      label: t('documents.columns.scope'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {t(`documents.scopes.${row.scope}`)}
          {row.faculty ? ` · ${valueFor(row.faculty.name, locale)}` : ''}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('documents.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'file_size',
      label: t('documents.columns.size'),
      sortable: true,
      align: 'end',
      render: (row) => (
        <span className="whitespace-nowrap text-secondary">
          {formatFileSize(row.file_size, (key) => t(`documents.units.${key}`))}
        </span>
      ),
    },
    {
      key: 'download_count',
      label: t('documents.columns.downloads'),
      sortable: true,
      align: 'end',
      render: (row) => row.download_count.toLocaleString(),
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) => {
        if (row.deleted_at) {
          return (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                act.mutate({ id: row.id, action: 'restore' });
              }}
            >
              {t('list.restore')}
            </Button>
          );
        }

        return mayDelete ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={(event) => {
              event.stopPropagation();
              setDeleting(row);
            }}
          >
            {t('common.delete')}
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.documents')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('documents.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        filters={
          <>
            <Select
              label={t('documents.columns.category')}
              hideLabel
              value={String(list.params.category_id ?? '')}
              onChange={(event) => list.setFilter('category_id', event.target.value || null)}
              options={[
                { value: '', label: t('documents.filters.anyCategory') },
                ...(categories.data?.data ?? []).map((category) => ({
                  value: String(category.id),
                  label: valueFor(category.name, locale),
                })),
              ]}
            />

            <Select
              label={t('documents.columns.scope')}
              hideLabel
              value={String(list.params.scope ?? '')}
              onChange={(event) => list.setFilter('scope', event.target.value || null)}
              options={[
                { value: '', label: t('documents.filters.anyScope') },
                ...(['university', 'faculty', 'vocational', 'quality_assurance'] as const).map(
                  (scope) => ({ value: scope, label: t(`documents.scopes.${scope}`) }),
                ),
              ]}
            />

            <Select
              label={t('documents.columns.status')}
              hideLabel
              value={String(list.params.status ?? '')}
              onChange={(event) => list.setFilter('status', event.target.value || null)}
              options={[
                { value: '', label: t('documents.filters.anyStatus') },
                ...(['draft', 'scheduled', 'published', 'archived'] as const).map((status) => ({
                  value: status,
                  label: t(`editor.publishing.statuses.${status}`),
                })),
              ]}
            />
          </>
        }
        primaryAction={
          can('documents.create') ? (
            <Button
              type="button"
              size="sm"
              iconLeft={<UploadCloud size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => setUploading(true)}
            >
              {t('documents.upload.title')}
            </Button>
          ) : null
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        sort={list.sort}
        onSortChange={list.setSort}
        onOpen={(row) => navigate(`/documents/${row.id}`)}
        emptyTitle={t('documents.empty.title')}
        emptyBody={t('documents.empty.body')}
      />

      {uploading ? <DocumentUploadDialog onClose={() => setUploading(false)} /> : null}

      {deleting ? (
        <DocumentDeleteDialog
          document={deleting}
          busy={act.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            act.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            )
          }
        />
      ) : null}
    </div>
  );
}
